// Recompra — top clientes, distribuição compras, recência, top PJ.
import { useEffect, useState } from "react";
import { api } from "../../api.ts";
import { BarChart, HorizontalBar } from "../../components/charts/index.ts";
import { fmtBRL, fmtNum, fmtDate } from "../../lib/format.ts";

interface Payload {
  summary: {
    total_with_purchase: number;
    recurring: number;
    one_timers: number;
  };
  top_customers: Array<{
    id: number; name: string | null; document_type: string | null; email: string | null;
    orders: number; revenue: number; last_order_at: string | null;
  }>;
  purchase_distribution: Array<{ bucket: string; customers: number }>;
  recency_buckets: Array<{ bucket: string; customers: number }>;
  top_pj: Array<{
    id: number; name: string; cnpj: string | null; state: string | null; city: string | null;
    total_spent: number; purchase_count: number; last_purchase_at: string | null; last_item_bought: string | null;
  }>;
}

export function RecompraPanel({ slug }: { slug: string }) {
  const [data, setData] = useState<Payload | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    api.get<Payload>(`/api/t/${slug}/tactical/recompra`).then(setData).catch((e) => setErr(e.message));
  }, [slug]);

  if (err) return <div className="card"><p style={{ color: "var(--red)" }}>erro: {err}</p></div>;
  if (!data) return <div className="center-loading">carregando…</div>;

  const recurringPct = data.summary.total_with_purchase > 0
    ? (data.summary.recurring / data.summary.total_with_purchase) * 100
    : 0;

  const distData = data.purchase_distribution.map((d) => ({
    label: d.bucket, value: d.customers,
  }));
  const recencyData = data.recency_buckets.map((d) => ({
    label: d.bucket, value: d.customers,
  }));

  const topCustomersBar = data.top_customers
    .slice(0, 10)
    .map((c) => ({
      name: ((c.name ?? c.email ?? "(sem nome)") + (c.document_type === "cnpj" ? " · PJ" : "")).slice(0, 42),
      value: c.revenue,
    }));

  return (
    <>
      <div className="kpi-grid">
        <div className="kpi-card">
          <div className="label">Clientes que já compraram</div>
          <div className="value">{fmtNum(data.summary.total_with_purchase)}</div>
          <div className="meta"><span className="mono">total na base</span></div>
        </div>
        <div className="kpi-card">
          <div className="label">Recorrentes (2+ compras)</div>
          <div className="value">{fmtNum(data.summary.recurring)}</div>
          <div className="meta">
            <span className="mono">{recurringPct.toFixed(1)}% do total</span>
          </div>
        </div>
        <div className="kpi-card">
          <div className="label">Compraram 1x apenas</div>
          <div className="value">{fmtNum(data.summary.one_timers)}</div>
          <div className="meta"><span className="mono">público pra reativar</span></div>
        </div>
      </div>

      <div className="section">
        <div className="section-head">
          <div>
            <p className="eyebrow">Histórico completo</p>
            <h2>Top 10 clientes por receita</h2>
          </div>
        </div>
        <div className="card">
          <HorizontalBar
            data={topCustomersBar}
            height={Math.max(220, topCustomersBar.length * 32)}
            formatValue={fmtBRL}
          />
        </div>
      </div>

      <div className="section">
        <div className="section-head">
          <div>
            <p className="eyebrow">Padrão de recorrência</p>
            <h2>Distribuição de compras por cliente</h2>
          </div>
        </div>
        <div className="grid g2">
          <div className="card">
            <h3>Quantos compraram quantas vezes</h3>
            <BarChart data={distData} height={220} />
          </div>
          <div className="card">
            <h3>Recência (última compra)</h3>
            <BarChart data={recencyData} height={220} />
          </div>
        </div>
      </div>

      {data.top_pj.length > 0 && (
        <div className="section">
          <div className="section-head">
            <div>
              <p className="eyebrow">Empresas</p>
              <h2>Top 10 clientes PJ</h2>
            </div>
          </div>
          <div className="card">
            <table className="simple">
              <thead>
                <tr>
                  <th>Empresa</th>
                  <th>UF</th>
                  <th style={{ textAlign: "right" }}>Total gasto</th>
                  <th style={{ textAlign: "right" }}>Compras</th>
                  <th>Última compra</th>
                </tr>
              </thead>
              <tbody>
                {data.top_pj.map((p) => (
                  <tr key={p.id}>
                    <td>
                      <strong>{p.name}</strong>
                      {p.last_item_bought && (
                        <div style={{ fontSize: ".74rem", color: "var(--text-muted)" }}>
                          últ. item: {p.last_item_bought.slice(0, 60)}
                        </div>
                      )}
                    </td>
                    <td className="mono">{p.state ?? "—"}</td>
                    <td style={{ textAlign: "right" }} className="mono">{fmtBRL(p.total_spent)}</td>
                    <td style={{ textAlign: "right" }} className="mono">{p.purchase_count}</td>
                    <td className="mono" style={{ fontSize: ".82rem" }}>{fmtDate(p.last_purchase_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </>
  );
}
